import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { selectManageRoles } from '../../../../../features/auth/authSlice';
import {
    useGetListAdministrationPagesQuery,
    useGetAdministrationPageByIdQuery,
    useSaveAdministrationPageMutation,
    useUpdateAdministrationPageMutation,
    useDeleteAdministrationPageMutation,
    useGetUserListQuery,
    useGetRoleListQuery,
} from '../../../../../api/endpoints/administratorPage';

const DEFAULT_FORM_DATA = {
    UserId: '',
    UserType: '',
    Authority: '',
    StartDate: new Date(),
    EndDate: null,
    Description: '',
    MailSend: 1,
    IsActive: 1
};

export const useSystemAdministrator = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const manageRoles = useSelector(selectManageRoles);
    const currentLanguageId = getCurrentLanguageId();
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [displayModal, setDisplayModal] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
    const [originalData, setOriginalData] = useState(null);
    const [prefillUserId, setPrefillUserId] = useState(null);


    const [filters, setFilters] = useState({
        firstName: '',
        lastName: '',
        role: null
    });

    const {
        data: administrationPagesData,
        isLoading: isLoadingPages,
        isFetching: isFetchingPages,
        error: pagesError,
        refetch: refetchPages
    } = useGetListAdministrationPagesQuery({
        PageNumber: pageNumber + 1, // backend 1-indexli; tablo pageNumber'ı 0-indexli tutar
        PageSize: pageSize,
        SearchText: searchText,
        LanguageId: currentLanguageId,
        FirstName: filters.firstName || undefined,
        LastName: filters.lastName || undefined,
        Role: filters.role ?? undefined,
        SortField: sortField || undefined,
        SortOrder: sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : undefined
    }, {
        refetchOnReconnect: false,
        refetchOnMountOrArgChange: true
    });

    const {
        data: selectedAdminData,
        isLoading: isLoadingSelectedAdmin,
        error: selectedAdminError
    } = useGetAdministrationPageByIdQuery(selectedAdmin?.id, {
        skip: !selectedAdmin?.id,
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
    });
    const {
        data: userList,
        isLoading: isLoadingUsers,
        isFetching: isFetchingUsers,
        error: usersError
    } = useGetUserListQuery({
        languageId: currentLanguageId
    }, {
        // GetUserList yalnızca Yeni Kayıt/Düzenle dialog'undaki kullanıcı dropdown'unu beslemek için çağrılır.
        // Dropdown tüm kullanıcı listesini alır ve kendi içinde (client-side) arar.
        // Sayfa listesi ve sayfa filtresi GetAll kullanır; bu yüzden dialog kapalıyken GetUserList çağrılmaz.
        skip: !displayModal
    });
    
    const {
        data: roleList,
        isLoading: isLoadingRoles,
        error: rolesError
    } = useGetRoleListQuery(currentLanguageId);

    const [saveAdministrationPage, { isLoading: isSaving }] = useSaveAdministrationPageMutation();
    const [updateAdministrationPage, { isLoading: isUpdating }] = useUpdateAdministrationPageMutation();
    const [deleteAdministrationPage, { isLoading: isDeleting }] = useDeleteAdministrationPageMutation();
    const canUpdateUserRole = useMemo(
        () => Array.isArray(manageRoles) && manageRoles.includes('UpdateRole'),
        [manageRoles]
    );


    const allItems = useMemo(() =>
        administrationPagesData?.data?.items || administrationPagesData?.items || [],
        [administrationPagesData]
    );

    // Filtreleme / sıralama / sayfalama backend'de (server-side) yapılır.
    // Sunucudan gelen sayfa item'ları tablo satır şekline map edilir.
    const totalRecords = administrationPagesData?.totalCount ?? 0;

    const administrationPages = useMemo(() => allItems.map((item) => ({
        rowKey: item.id,
        userId: item.userId,
        assignmentId: item.id,
        hasAssignment: true,
        fullName: item.fullName,
        role: item.userType,
        authority: item.authority,
        startDate: item.startDate,
        endDate: item.endDate,
        userType: item.userType,
        description: item.description,
        mailSend: item.mailSend,
        isActive: item.isActive,
    })), [allItems]);

    const createFormData = useCallback((admin) => ({
        UserId: admin.userId || '',
        UserType: admin.userType || '',
        Authority: admin.authority !== undefined ? admin.authority : '',
        StartDate: admin.startDate ? new Date(admin.startDate) : new Date(),
        EndDate: admin.endDate ? new Date(admin.endDate) : null,
        Description: admin.description || '',
        MailSend: admin.mailSend ?? 1,
        IsActive: admin.isActive ?? 1
    }), []);

    const hasFormChanged = useCallback(() => {
        if (!originalData) return true;
        const fields = ['UserId', 'UserType', 'Authority', 'Description', 'MailSend', 'IsActive'];
        const hasFieldChanged = fields.some(field => originalData[field] !== formData[field]);
        if (hasFieldChanged) return true;
        const originalStartTime = originalData.StartDate?.getTime() || null;
        const currentStartTime = formData.StartDate?.getTime() || null;
        const originalEndTime = originalData.EndDate?.getTime() || null;
        const currentEndTime = formData.EndDate?.getTime() || null;
        return originalStartTime !== currentStartTime || originalEndTime !== currentEndTime;
    }, [formData, originalData]);

    const validateForm = useCallback(() => {
        const errors = [];
        if (!formData.UserId) {
            errors.push(th('systemAdministrator.validation.userRequired'));
        }
        if (formData.Authority === null || formData.Authority === undefined || formData.Authority === '') {
            errors.push(th('systemAdministrator.validation.userTypeRequired'));
        }
        if (!formData.StartDate) {
            errors.push(th('systemAdministrator.validation.startDateRequired'));
        }
        if (formData.IsActive === null || formData.IsActive === undefined) {
            errors.push(th('systemAdministrator.validation.statusRequired'));
        }
        if (formData.MailSend === null || formData.MailSend === undefined) {
            errors.push(th('systemAdministrator.validation.mailSendRequired'));
        }
        return errors;
    }, [formData, th]);

    useEffect(() => {
        if (selectedAdmin && !selectedAdminData && !isLoadingSelectedAdmin) {
            const data = createFormData(selectedAdmin);
            setFormData(data);
            setOriginalData(data);
        } else if (selectedAdminData) {
            const admin = selectedAdminData.data || selectedAdminData;
            const data = createFormData(admin);
            setFormData(data);
            setOriginalData(data);
        } else if (!selectedAdmin) {
            setFormData(prefillUserId ? { ...DEFAULT_FORM_DATA, UserId: prefillUserId } : DEFAULT_FORM_DATA);
            setOriginalData(null);
        }
    }, [selectedAdmin, selectedAdminData, isLoadingSelectedAdmin, createFormData, prefillUserId]);

    useEffect(() => {
        refetchPages();
    }, [currentLanguageId, refetchPages]);

    const handleNew = useCallback(() => {
        setSelectedAdmin(null);
        setPrefillUserId(null);
        setFormData(DEFAULT_FORM_DATA);
        setOriginalData(null);
        setDisplayModal(true);
    }, []);

    const handleEdit = useCallback((row) => {
        if (row?.assignmentId) {
            setPrefillUserId(null);
            setSelectedAdmin({ ...row, id: row.assignmentId });
            setDisplayModal(true);
        } else {
            setSelectedAdmin(null);
            setPrefillUserId(row?.userId ?? null);
            setFormData({ ...DEFAULT_FORM_DATA, UserId: row?.userId ?? '' });
            setOriginalData(null);
            setDisplayModal(true);
        }
    }, []);

    const handleSave = useCallback(async () => {
        const errors = validateForm();
        if (errors.length > 0) {
            throw new Error(errors[0]);
        }
        if (selectedAdmin && !hasFormChanged()) {
            throw new Error(th('systemAdministrator.validation.noChanges'));
        }
        const saveData = {
            UserId: formData.UserId,
            StartDate: formData.StartDate,
            Authority: formData.Authority,
            EndDate: formData.EndDate,
            Description: formData.Description,
            MailSend: formData.MailSend,
            IsActive: formData.IsActive
        };
        if (selectedAdmin) {
            await updateAdministrationPage({
                Id: selectedAdmin.id,
                ...saveData
            }).unwrap();
        } else {
            await saveAdministrationPage(saveData).unwrap();
        }
        setDisplayModal(false);
        refetchPages();
    }, [formData, selectedAdmin, hasFormChanged, validateForm, saveAdministrationPage, updateAdministrationPage, refetchPages, th]);

    const handleDelete = useCallback(async () => {
        if (selectedAdmin?.id) {
            await deleteAdministrationPage(selectedAdmin.id).unwrap();
            setDisplayModal(false);
            refetchPages();
        }
    }, [selectedAdmin, deleteAdministrationPage, refetchPages]);

    const handleSearch = useCallback((searchValue) => {
        setSearchText(searchValue);
        setPageNumber(0);
    }, []);

    const handlePageChange = useCallback((event) => {
        setPageNumber(event.page);
        setPageSize(event.rows);
    }, []);


    const handleSort = useCallback((field, order) => {
        setSortField(field);
        setSortOrder(order);
        setPageNumber(0);
    }, []);
     const handleAdvancedFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        setPageNumber(0);
    }, []);

    const handleResetFilters = useCallback(() => {
        setFilters({
            firstName: '',
            lastName: '',
            role: null
        });
        setSearchText('');
        setPageNumber(0);
    }, []);

    const closeModal = useCallback(() => {
        setDisplayModal(false);
        setSelectedAdmin(null);
        setPrefillUserId(null);
        setFormData(DEFAULT_FORM_DATA);
        setOriginalData(null);
    }, []);

    return {
        // Data
        administrationPages,
        userList: userList || [],
        roleList: roleList || [],
        selectedAdmin,
        formData,
        displayModal,
        filters,
        // Loading states
        isLoading: isLoadingPages || isFetchingPages,
        isLoadingUsers,
        isFetchingUsers,
        isLoadingRoles,
        isLoadingSelectedAdmin,
        isSaving,
        isUpdating,
        isDeleting,
        canUpdateUserRole,
        // Pagination
        pageNumber,
        pageSize,
        totalRecords,
        // Event handlers
        handleNew,
        handleEdit,
        handleSave,
        handleDelete,
        handleSearch,
        handlePageChange,
        handleSort,
        handleAdvancedFilterChange,
        handleResetFilters,
        closeModal,
        setFormData,
        // Errors
        error: pagesError,
        usersError,
        rolesError,
        selectedAdminError
    };
};